// ordercheckoutpop.tsx  — UI-only refresh (Bootstrap), functionality unchanged
import React, { useCallback, useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Variant, Product } from "types";
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import PaginationControls from "components/PaginationControls";
import Select, { components } from "react-select";
import "../assets/css/SignageOrders.css"
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import imageCustomSquare from "assets/images/imagecustomrounded.png";
import { uploadFilesAndGetIds, hardDeletePresignedFiles } from "components/Imageshelper";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { showError } from "toast/toast";
import { getAuthCookie } from "../utils/authCookies";
import {
  createCustomizationContext,
  instantiateDesignFromTemplate,
  type CustomizationContextSource,
} from "api/realesyStudioApi";
import { studioBatchVariantKey } from "utils/signageStudioBatchRestore";

type CustomField = {
  _id?: string;
  field_name: string;
  field_type: "text" | "file";
  required?: boolean;
  value?: string;
};

export type ProductObj = {
  product_id: string;
  product: Product;
  available?: number;
  quantity?: number;
  customizations?: { fields?: CustomField[] };
};

function toAvailableQty(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function getRowAvailableQty(
  productObj?: ProductObj | null,
  selectedVariant?: { inventory?: Array<{ available?: number }> } | null
): number {
  const variantInventory = (selectedVariant as { inventory?: Array<{ available?: number }> } | undefined)
    ?.inventory;
  if (Array.isArray(variantInventory) && variantInventory.length > 0) {
    return variantInventory.reduce((sum, loc) => sum + toAvailableQty(loc?.available), 0);
  }
  return toAvailableQty(productObj?.available);
}

/** One line from GET /customization-batches/:id `items[]` — used to show Studio preview + Edit design in product pickers. */
export type StudioBatchLineItem = {
  product_id: string;
  variant_id: string;
  design_id?: string;
  preview_id?: string;
  design_name?: string;
  /** Batch item `active_context_id` — required to re-open Studio in edit mode. */
  context_id?: string;
};

/** Optional: send full order/installation snapshots when opening Studio from signage flows. */
export type StudioCustomizationFlowProps = {
  source: CustomizationContextSource;
  /** After first Studio return, URL includes `batch_id` — pass it when opening Studio for another product. */
  existingBatchId?: string;
  /** When set (e.g. after batch restore), slot product rows matching product_id+variant_id show `preview_id` thumb + Edit design. */
  batchLineItems?: StudioBatchLineItem[];
  getOrderSnapshot?: () => Record<string, unknown> | Promise<Record<string, unknown>>;
  getInstallationOrderSnapshot?: () => Record<string, unknown> | Promise<Record<string, unknown>>;
  onBeforeStudioRedirect?: () => void | Promise<void>;
};

export type StudioBatchVariantPreviewMap = Record<
  string,
  { previewFileId: string; designId: string; designName: string }
>;

type Props = {
  open: boolean;
  onClose: () => void;
  supplierId: any;
  createdById: string;
  onAdd: (payload: any) => void;
  /** `productId:variantId` keys already on the order — blocks duplicate add in the modal. */
  existingLineKeys?: string[];
  /** When returning from Studio: map `product_id|variant_id` → work-drive preview file id (requires batch `design_id`). */
  studioBatchVariantPreviews?: StudioBatchVariantPreviewMap | null;
  studioCustomization?: StudioCustomizationFlowProps;
};

type PaginationState = { pageIndex: number; pageSize: number };

const columnHelper = createColumnHelper<ProductObj>();

// helpers (unchanged)
const getRowId = (productobj: ProductObj, idx: number) =>
  productobj?.product_id || String(idx);

/** Work-drive file id (24 hex) — not arbitrary customization text. */
const isCloudFileId = (s: string) => /^[0-9a-f]{24}$/i.test(s.trim());

const buildAvailableAttributes = (variants: Variant[]) => {
  const map: Record<string, Set<string>> = {};
  variants.forEach((v) => {
    Object.entries(v.attributes || {}).forEach(([k, val]) => {
      (map[k] ??= new Set()).add(String(val).trim());
    });
  });
  const out: Record<string, string[]> = {};
  Object.keys(map).forEach((k) => (out[k] = Array.from(map[k])));
  return out;
};

/** Variant has at least one inventory location (non-empty inventory array). */
const variantHasInventory = (v: Variant) =>
  Array.isArray(v?.inventory) && v.inventory.length > 0;

/** First variant that has inventory, or first variant if none have inventory. */
const firstVariantWithInventory = (variants: Variant[]): Variant | undefined =>
  variants.find(variantHasInventory) ?? variants[0];

/** Build attribute options for select with isDisabled when no variant with that attribute value has inventory. */
const getAttributeOptionsWithInventory = (
  variants: Variant[],
  availableAttributes: Record<string, string[]>
): Record<string, { value: string; label: string; isDisabled: boolean }[]> => {
  const out: Record<string, { value: string; label: string; isDisabled: boolean }[]> = {};
  Object.entries(availableAttributes || {}).forEach(([key, values]) => {
    out[key] = (values || []).map((value) => ({
      value,
      label: value,
      isDisabled: !variants.some(
        (v) =>
          String(v.attributes?.[key] ?? "").trim() === String(value).trim() &&
          variantHasInventory(v)
      ),
    }));
  });
  return out;
};

const findVariantDetails = (
  variants: Variant[],
  selAttrs: Record<string, string>
): Variant | undefined =>
  variants.find((variant) =>
    Object.entries(selAttrs).every(
      ([k, v]) =>
        String(variant.attributes?.[k] ?? "").trim() === String(v).trim()
    )
  );

/** Variant used for Customize / studio: row state, attribute match, or default with stock. */
const resolveVariantForCustomizeRow = (
  productObj: ProductObj,
  rowId: string,
  selectedVariantByRow: Record<string, Variant | null | undefined>,
  selectedAttributesByRow: Record<string, Record<string, string>>
): Variant | undefined => {
  const p = productObj?.product || {};
  const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
  if (!variants.length) return undefined;
  const picked = selectedVariantByRow[rowId];
  if (picked) return picked;
  const attrs = selectedAttributesByRow[rowId] || {};
  // Empty attrs: `findVariantDetails` would always match variants[0] — wrong vs seed (firstVariantWithInventory).
  if (Object.keys(attrs).length > 0) {
    const matched = findVariantDetails(variants, attrs);
    if (matched) return matched;
  }
  return firstVariantWithInventory(variants) ?? variants[0];
};

type VariantAttrOption = { value: string; label: string; isDisabled?: boolean };

const VARIANT_ATTR_SELECT_H = 36;

const variantAttrSelectTheme = (t: any) => ({
  ...t,
  colors: {
    ...t.colors,
    primary: "#9ca3af",
    primary25: "rgba(156,163,175,0.15)",
    primary50: "rgba(156,163,175,0.25)",
  },
});

function VariantAttrDropdownIndicator(props: any) {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-6" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
}

function VariantAttrSingleValue(props: any) {
  const label = String(props.data?.label ?? props.children ?? "");
  return (
    <components.SingleValue {...props}>
      <span title={label}>{props.children}</span>
    </components.SingleValue>
  );
}

/** Plain react-select for variant attributes (replaces UIReactSelectPOP). */
const OrderCheckoutVariantSelect = ({
  name,
  value,
  options,
  placeholder,
  onChange,
  className,
}: {
  name: string;
  value: VariantAttrOption | null;
  options: VariantAttrOption[];
  placeholder: string;
  onChange: (opt: VariantAttrOption | null) => void;
  className?: string;
}) => {
  /**
   * Do NOT portal to document.body with position:fixed — in modals + scrollable tables the menu
   * often misaligns horizontally. Inline absolute menu tracks the control; `ocv` avoids global `.rs__*` rules.
   */
  const styles = useMemo(
    () => ({
      control: (base: any, state: any) => ({
        ...base,
        minHeight: VARIANT_ATTR_SELECT_H,
        height: VARIANT_ATTR_SELECT_H,
        minWidth: 0,
        width: "100%",
        borderRadius: 8,
        alignItems: "center",
        overflow: "hidden",
        backgroundColor: state.isFocused ? "#fafafa" : "#fff",
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: state.isFocused ? "#cbd5e1" : "#e7eaf3",
        boxShadow: "none",
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        zIndex: 0,
      }),
      valueContainer: (base: any) => ({
        ...base,
        padding: "0 10px",
        minHeight: VARIANT_ATTR_SELECT_H,
        height: VARIANT_ATTR_SELECT_H,
        display: "flex",
        alignItems: "center",
        flex: 1,
        minWidth: 0,
        flexWrap: "nowrap",
        overflow: "hidden",
        position: "relative",
        zIndex: 0,
      }),
      singleValue: (base: any) => ({
        ...base,
        position: "static" as const,
        top: "auto",
        transform: "none",
        margin: 0,
        minWidth: 0,
        maxWidth: "100%",
        flex: "1 1 auto",
        lineHeight: 1.35,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        wordBreak: "normal",
        overflowWrap: "normal",
        zIndex: 0,
      }),
      placeholder: (base: any) => ({
        ...base,
        margin: 0,
        lineHeight: 1.25,
        color: "#6b7280",
        zIndex: 0,
      }),
      input: (base: any) => ({
        ...base,
        margin: 0,
        padding: 0,
        zIndex: 0,
      }),
      indicatorsContainer: (base: any) => ({
        ...base,
        paddingRight: 4,
        minHeight: VARIANT_ATTR_SELECT_H,
        height: "auto",
        alignItems: "center",
        alignSelf: "center",
        flexShrink: 0,
        zIndex: 0,
      }),
      indicatorSeparator: () => ({ display: "none" }),
      dropdownIndicator: (base: any) => ({
        ...base,
        padding: "0 6px",
        zIndex: 0,
      }),
      menu: (base: any) => ({
        ...base,
        borderRadius: 8,
        zIndex: 0,
        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.12)",
        /* Match dropdown width to the control (avoids narrow / offset menu vs field) */
        minWidth: "100%",
        width: "100%",
        left: 0,
        marginLeft: 0,
        marginRight: 0,
      }),
      menuList: (base: any) => ({
        ...base,
        maxHeight: 200,
        zIndex: 0,
      }),
      option: (base: any, state: any) => {
        const disabled = Boolean(state.isDisabled);
        return {
          ...base,
          whiteSpace: "normal",
          wordBreak: "break-word" as const,
          overflowWrap: "anywhere" as const,
          lineHeight: 1.35,
          fontSize: 13,
          cursor: disabled ? "not-allowed" : "pointer",
          color: disabled ? "#94a3b8" : "#111827",
          zIndex: 0,
          backgroundColor: disabled
            ? "#f8fafc"
            : state.isSelected
              ? "#e9edf7"
              : state.isFocused
                ? "#f3f4f6"
                : "#fff",
        };
      },
    }),
    []
  );

  return (
    <div className={className} style={{ minWidth: 0, width: "100%" }}>
      <Select<VariantAttrOption, false>
        inputId={name}
        name={name}
        options={options}
        value={value}
        onChange={(opt) => onChange((opt as VariantAttrOption) ?? null)}
        placeholder={placeholder}
        classNamePrefix="ocv"
        styles={styles}
        theme={variantAttrSelectTheme}
        components={{ DropdownIndicator: VariantAttrDropdownIndicator, SingleValue: VariantAttrSingleValue }}
        isSearchable={false}
        isClearable={false}
        blurInputOnSelect
        menuPosition="absolute"
        /* "auto" opens upward inside the scrollable table when space below looks tight */
        menuPlacement="bottom"
        menuShouldScrollIntoView={false}
        /* false: true blocks wheel on scroll parents (e.g. modal table) while menu is open */
        menuShouldBlockScroll={false}
      />
    </div>
  );
};

const OrderCheckoutPop: React.FC<Props> = ({
  open,
  onClose,
  supplierId,
  createdById,
  onAdd,
  existingLineKeys,
  studioBatchVariantPreviews,
  studioCustomization,
}) => {
  const token = getAuthCookie("sessionToken") || "";

  // server data
  const [supplierProducts, setSupplierProducts] = useState<ProductObj[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");

  // table state
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // expansion & validation
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [rowValidity, setRowValidity] = useState<Record<string, boolean>>({});

  // per-row values
  const [rowCustomValues, setRowCustomValues] = useState<
    Record<string, Record<string, string | File | null>>
  >({});
  const [rowFilePreviews, setRowFilePreviews] = useState<
    Record<string, Record<string, string>>
  >({});

  // per-row variant/attributes
  const [selectedVariantByRow, setSelectedVariantByRow] = useState<
    Record<string, Variant | null>
  >({});
  const [selectedAttributesByRow, setSelectedAttributesByRow] = useState<
    Record<string, Record<string, string>>
  >({});
  const [availableAttributesByRow, setAvailableAttributesByRow] = useState<
    Record<string, Record<string, string[]>>
  >({});
  type UploadedImage = { id: string; name: string; size: number };
  const [uploaded, setUploaded] = useState<UploadedImage[]>([]);
  const [tempUploadIds, setTempUploadIds] = useState<Record<string, string[]>>({});
  const [studioInstantiateRowId, setStudioInstantiateRowId] = useState<string | null>(null);

  const openCustomizeInStudio = useCallback(
    async (
      templateId: string,
      rowId: string,
      productId: string,
      variantId: string
    ) => {
      const studioBaseUrl = (process.env.REACT_APP_STUDIO_BASE_URL || "").trim().replace(/\/+$/, "");
      const vendorId = String(supplierId ?? "").trim();
      const tid = String(templateId ?? "").trim();
      if (!tid || !studioBaseUrl || !vendorId) {
        showError("Cannot open Studio: missing template, studio URL, or supplier.");
        return;
      }
      try {
        setStudioInstantiateRowId(rowId);

        if (studioCustomization?.source) {
          await studioCustomization.onBeforeStudioRedirect?.();
          const instantiateResult = await instantiateDesignFromTemplate(tid, vendorId);
          const designId = String(instantiateResult.design_id ?? "").trim();
          if (!designId) {
            throw new Error("Design id not returned from instantiate API");
          }
          const orderSnap =
            studioCustomization.source === "signage_create_order"
              ? (await studioCustomization.getOrderSnapshot?.()) ?? {}
              : {};
          const installationSnap =
            studioCustomization.source === "signage_add_installation"
              ? (await studioCustomization.getInstallationOrderSnapshot?.()) ?? {}
              : {};
          const existingBatchId = String(studioCustomization.existingBatchId ?? "").trim();
          const { batch_id: batchId, context_id: contextId } = await createCustomizationContext({
            source: studioCustomization.source,
            product_id: String(productId ?? "").trim(),
            variant_id: String(variantId ?? "").trim(),
            template_id: designId,
            line_item_key: String(rowId ?? "").trim(),
            mode: "create",
            order: orderSnap as Record<string, unknown>,
            installation_order: installationSnap as Record<string, unknown>,
            ...(existingBatchId ? { batch_id: existingBatchId } : {}),
          });
          // Strip existing batch/context params from return path so previous batch IDs are not re-used.
          const rawReturn = `${window.location.pathname}${window.location.search || ""}`;
          const _u = new URL(rawReturn, window.location.origin);
          _u.searchParams.delete("batch_id");
          _u.searchParams.delete("context_id");
          const returnPath = `${_u.pathname}${_u.search || ""}`;
          const studioUrl = new URL(`${studioBaseUrl}/open-design/${encodeURIComponent(designId)}`);
          studioUrl.searchParams.set("batch_id", batchId);
          studioUrl.searchParams.set("context_id", contextId);
          studioUrl.searchParams.set("return_path", returnPath);
          window.location.assign(studioUrl.toString());
          return;
        }

        const result = await instantiateDesignFromTemplate(tid, vendorId);
        const rawReturn = `${window.location.pathname}${window.location.search || ""}`;
        const _u2 = new URL(rawReturn, window.location.origin);
        _u2.searchParams.delete("batch_id");
        _u2.searchParams.delete("context_id");
        const returnPath = `${_u2.pathname}${_u2.search || ""}`;
        const studioUrl = new URL(
          `${studioBaseUrl}/open-design/${encodeURIComponent(result.design_id)}`
        );
        studioUrl.searchParams.set("return_path", returnPath);
        window.location.assign(studioUrl.toString());
      } catch (e) {
        console.error(e);
        showError(e instanceof Error ? e.message : "Failed to open Studio");
      } finally {
        setStudioInstantiateRowId(null);
      }
    },
    [supplierId, studioCustomization]
  );

  // Collect ids we might display
  const allImageIds: string[] = useMemo(() => {
    const ids: string[] = [];

    // product images from rows
    (supplierProducts || []).forEach((r) => {
      const id = r?.product?.image?.url || (r?.product?.images?.length ? r.product.images[0]?.url : "");
      if (id) ids.push(String(id));
    });

    // Custom file field values only (uploaded work-drive ids), not text-field typing
    Object.values(rowCustomValues || {}).forEach((rowObj: any) => {
      Object.values(rowObj || {}).forEach((val: any) => {
        if (typeof val === "string" && val.trim() && isCloudFileId(val)) ids.push(val.trim());
      });
    });

    // newly uploaded in this session
    uploaded.forEach((u) => ids.push(u.id));

    if (studioBatchVariantPreviews) {
      Object.values(studioBatchVariantPreviews).forEach((e) => {
        const id = String(e?.previewFileId ?? "").trim();
        if (id && isCloudFileId(id)) ids.push(id);
      });
    }

    return Array.from(new Set(ids));
  }, [supplierProducts, rowCustomValues, uploaded, studioBatchVariantPreviews]);

  // Payload expected by useVariantImageUrlMap
  const dataForMap = useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [{
      product: { variants: [{ images: allImageIds.map((url) => ({ url })) }] },
    }];
  }, [allImageIds]);

  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);
  // fetch (unchanged)
  useEffect(() => {
    if (!open) return;
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${supplierId}/inventory/products-by-brands/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              user_id: createdById,
              page: globalFilter?.trim() ? 1 : pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {},
              sort: { field: "", order: "desc" },
            }),
          }
        );
        const result = await res.json();
        const data = Array.isArray(result?.data)
          ? (result.data as ProductObj[])
          : [];
        setSupplierProducts(data);
        setRowCount(Number(result?.total ?? data.length));
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to fetch products");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [open, supplierId, token, pagination.pageIndex, pagination.pageSize, globalFilter]);

  // row utils (unchanged)
  const getCustomizationFields = (productobj: ProductObj, rowId: string): CustomField[] => {
    const p = productobj?.product || {};
    const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
    const selected = selectedVariantByRow[rowId];
    if (selected?.customizations?.fields?.length) return selected.customizations.fields;
    if (variants[0]?.customizations?.fields?.length) return variants[0].customizations!.fields;
    if (productobj?.customizations?.fields?.length) return productobj.customizations!.fields!;
    return [];
  };

  const validateRow = (
    rowId: string,
    fields: CustomField[],
    values: Record<string, string | File | null>
  ) => {
    const ok = fields.every((f) => {
      if (!f.required) return true;
      const v = values?.[f.field_name];

      if (f.field_type === "text") {
        return typeof v === "string" && v.trim().length > 0;
      }

      // file field: accept File object OR an already-uploaded id string
      return (v instanceof File) || (typeof v === "string" && v.trim().length > 0);
    });

    setRowValidity((prev) => ({ ...prev, [rowId]: ok }));
    return ok;
  };

  /** Batch return: this product+variant already has a Studio design (preview in map). */
  const isBatchStudioDesignForRow = useCallback(
    (productobj: ProductObj, rowId: string): boolean => {
      if (!studioBatchVariantPreviews || !Object.keys(studioBatchVariantPreviews).length) return false;
      const resolved = resolveVariantForCustomizeRow(
        productobj,
        rowId,
        selectedVariantByRow,
        selectedAttributesByRow
      );
      const vid = String((resolved as { variant_id?: string } | undefined)?.variant_id ?? "").trim();
      const pid = String(productobj?.product_id ?? "").trim();
      if (!pid || !vid) return false;
      const ent = studioBatchVariantPreviews[studioBatchVariantKey(pid, vid)];
      return !!(ent?.previewFileId && ent?.designId);
    },
    [selectedVariantByRow, selectedAttributesByRow, studioBatchVariantPreviews]
  );

  const openConfigurator = (productobj: ProductObj, rowId: string) => {
    if (expandedRowId === rowId) {
      setExpandedRowId(null);
      return;
    }

    setExpandedRowId(rowId);

    const p = productobj?.product || {};
    const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];

    if (!variants.length) {
      setAvailableAttributesByRow((pv) => ({ ...pv, [rowId]: {} }));
      setSelectedAttributesByRow((pv) => ({ ...pv, [rowId]: {} }));
      setSelectedVariantByRow((pv) => ({ ...pv, [rowId]: null }));
      const fields = getCustomizationFields(productobj, rowId);
      const initVals = rowCustomValues[rowId] || {};
      validateRow(rowId, fields, initVals);
      return;
    }

    const attrs = buildAvailableAttributes(variants);
    setAvailableAttributesByRow((pv) => ({ ...pv, [rowId]: attrs }));

    const attrKeys = Object.keys(attrs);
    const existingAttrs = { ...(selectedAttributesByRow[rowId] || {}) };
    const existingVariant = selectedVariantByRow[rowId];
    const hasUserSelection =
      !!existingVariant ||
      attrKeys.some((k) => String(existingAttrs[k] ?? "").trim() !== "");

    if (hasUserSelection) {
      let variantToUse = existingVariant ?? null;
      const hasAllAttrs =
        attrKeys.length > 0 &&
        attrKeys.every((k) => String(existingAttrs[k] ?? "").trim() !== "");
      if (hasAllAttrs) {
        const matched = findVariantDetails(variants, existingAttrs);
        if (matched) variantToUse = matched;
      } else if (!variantToUse) {
        const partial = findVariantDetails(variants, existingAttrs);
        if (partial) variantToUse = partial;
      }
      if (variantToUse) {
        setSelectedVariantByRow((pv) => ({ ...pv, [rowId]: variantToUse }));
      }
      setSelectedAttributesByRow((pv) => ({ ...pv, [rowId]: existingAttrs }));
    } else {
      const def = firstVariantWithInventory(variants) ?? variants[0];
      setSelectedVariantByRow((pv) => ({ ...pv, [rowId]: def }));

      const defAttrs: Record<string, string> = {};
      Object.entries(def?.attributes || {}).forEach(
        ([k, v]) => (defAttrs[k] = String(v).trim())
      );
      setSelectedAttributesByRow((pv) => ({ ...pv, [rowId]: defAttrs }));
    }

    const initVals = rowCustomValues[rowId] || {};
    const fields = getCustomizationFields(productobj, rowId);
    validateRow(rowId, fields, initVals);
  };

  const handleAttributeChange = (
    productobj: ProductObj,
    rowId: string,
    key: string,
    value: string
  ) => {
    setSelectedAttributesByRow((prev) => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [key]: value },
    }));

    const p = productobj?.product || {};
    const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
    const allAttrs = availableAttributesByRow[rowId] || {};
    const updated = { ...(selectedAttributesByRow[rowId] || {}), [key]: value };

    if (
      Object.keys(updated).length === Object.keys(allAttrs).length &&
      variants.length
    ) {
      const matched = findVariantDetails(variants, updated);
      setSelectedVariantByRow((pv) => ({ ...pv, [rowId]: matched || null }));
      const fields: CustomField[] =
        (matched?.customizations?.fields?.length ? matched.customizations!.fields! : []) ||
        (variants[0]?.customizations?.fields?.length ? variants[0].customizations!.fields! : []) ||
        (productobj?.customizations?.fields?.length ? productobj.customizations!.fields! : []);
      const values = rowCustomValues[rowId] || {};
      validateRow(rowId, fields, values);
    }
  };

  const handleTextChange = (
    productobj: ProductObj,
    rowId: string,
    field: CustomField,
    value: string
  ) => {
    setRowCustomValues((prev) => {
      const next = { ...(prev[rowId] || {}), [field.field_name]: value };
      const all = { ...prev, [rowId]: next };
      const fields = getCustomizationFields(productobj, rowId);
      validateRow(rowId, fields, next);
      return all;
    });
  };

  const handleFileChange = (
    productobj: any,
    rowId: string,
    field: CustomField,
    file: File | null
  ) => {
    // store the File (or null) in row values for this field
    setRowCustomValues(prev => {
      const nextRow = { ...(prev[rowId] || {}), [field.field_name]: file };
      const all = { ...prev, [rowId]: nextRow };
      const fields = getCustomizationFields(productobj, rowId);
      validateRow(rowId, fields, nextRow);
      return all;
    });

    // show preview using a local blob url (no upload here)
    setRowFilePreviews(prev => {
      const curr = prev[rowId] || {};
      const prevUrl = curr[field.field_name];
      if (prevUrl && (prevUrl.startsWith("blob:") || prevUrl.startsWith("data:"))) {
        try { URL.revokeObjectURL(prevUrl); } catch { }
      }
      const next = { ...curr };
      if (file) next[field.field_name] = URL.createObjectURL(file);
      else delete next[field.field_name];
      return { ...prev, [rowId]: next };
    });
  };



  //   const handleFileChange = async (
  //   productobj: any,
  //   rowId: string,
  //   field: CustomField,
  //   file: File | null
  // ) => {
  //   const prevVal = (rowCustomValues[rowId] || {})[field.field_name];

  //   // cleared
  //   if (!file) {
  //     if (typeof prevVal === "string" && prevVal.trim()) {
  //       try { await hardDeletePresignedFiles([prevVal], token); } catch {}
  //     }
  //     setRowCustomValues(prev => {
  //       const next = { ...(prev[rowId] || {}), [field.field_name]: null };
  //       return { ...prev, [rowId]: next };
  //     });
  //     setRowFilePreviews(prev => {
  //       const curr = prev[rowId] || {};
  //       const { [field.field_name]: _, ...rest } = curr;
  //       return { ...prev, [rowId]: rest };
  //     });
  //     const fields = getCustomizationFields(productobj, rowId);
  //     validateRow(rowId, fields, (rowCustomValues[rowId] || {}));
  //     return;
  //   }

  //   // new file picked → upload immediately
  //   try {
  //     const [id] = await uploadFilesAndGetIds([file], token || null);

  //     // hard-delete old temp id if replaced
  //     if (typeof prevVal === "string" && prevVal && prevVal !== id) {
  //       try { await hardDeletePresignedFiles([prevVal], token); } catch {}
  //     }

  //     // store as field value (id string)
  //     setRowCustomValues(prev => {
  //     const nextRow = { ...(prev[rowId] || {}), [field.field_name]: file };
  //     const all = { ...prev, [rowId]: nextRow };
  //     const fields = getCustomizationFields(productobj, rowId);
  //     validateRow(rowId, fields, nextRow);
  //     return all;
  //   });

  //   // 2) Update preview with a blob URL; DO NOT upload here
  //   setRowFilePreviews(prev => {
  //     const curr = prev[rowId] || {};
  //     // revoke old blob if any
  //     const prevUrl = curr[field.field_name];
  //     if (prevUrl && (prevUrl.startsWith("blob:") || prevUrl.startsWith("data:"))) {
  //       try { URL.revokeObjectURL(prevUrl); } catch {}
  //     }

  //     const next = { ...curr };
  //     if (file) next[field.field_name] = URL.createObjectURL(file);
  //     else delete next[field.field_name];

  //     return { ...prev, [rowId]: next };
  //   });
  //     // track temp ids per row (cleaned on cancel/close)
  //     setTempUploadIds(prev => {
  //       const arr = [...(prev[rowId] || [])];
  //       if (!arr.includes(id)) arr.push(id);
  //       return { ...prev, [rowId]: arr };
  //     });

  //     // let the map presign it
  //     setUploaded(prev => [...prev, { id, name: file.name, size: file.size }]);

  //     // immediate preview: presigned if ready, else BASE_URL fallback
  //     // const presigned =
  //     //   getImageSrc(id, imageUrlMap) ||
  //     //   (id?.startsWith("http") ? id : `${process.env.REACT_APP_BASE_URL}${id}`);

  //     // setRowFilePreviews(prev => ({
  //     //   ...prev,
  //     //   [rowId]: { ...(prev[rowId] || {}), [field.field_name]: presigned },
  //     // }));
  //   } catch (e) {
  //     console.error("Upload failed", e);
  //   }
  // };


  const handleRemoveCustomFile = (productobj: any, rowId: string, field: CustomField) => {
    // revoke local blob preview
    const prevUrl = rowFilePreviews[rowId]?.[field.field_name];
    if (prevUrl && (prevUrl.startsWith("blob:") || prevUrl.startsWith("data:"))) {
      try { URL.revokeObjectURL(prevUrl); } catch { }
    }

    // clear preview
    setRowFilePreviews(prev => {
      const curr = prev[rowId] || {};
      const { [field.field_name]: _, ...rest } = curr;
      return { ...prev, [rowId]: rest };
    });

    // clear value and revalidate
    setRowCustomValues(prev => {
      const nextRow = { ...(prev[rowId] || {}), [field.field_name]: null };
      const all = { ...prev, [rowId]: nextRow };
      const fields = getCustomizationFields(productobj, rowId);
      validateRow(rowId, fields, nextRow);
      return all;
    });
  };




  const handleAddConfigured = async (productobj: ProductObj) => {
    const p = productobj?.product || {};
    const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
    const rowId = productobj?.product_id || expandedRowId || "0";
    const hasAttrs = variants.some(
      (v) => v?.attributes && Object.keys(v.attributes).length > 0
    );
    const hasCust =
      variants.some(
        (v) =>
          Array.isArray(v?.customizations?.fields) &&
          v.customizations.fields.length > 0
      ) || Array.isArray(productobj?.customizations?.fields);
    const needsConfig = hasAttrs || hasCust;

    if (needsConfig && !isBatchStudioDesignForRow(productobj, rowId)) {
      const fields = getCustomizationFields(productobj, rowId);
      const values = rowCustomValues[rowId] || {};
      const ok = validateRow(rowId, fields, values);
      if (!ok) return;
    }

    const selectedVariant =
      resolveVariantForCustomizeRow(
        productobj,
        rowId,
        selectedVariantByRow,
        selectedAttributesByRow
      ) ??
      (variants.length
        ? firstVariantWithInventory(variants) ?? variants[0]
        : {
          variant_id: "",
          variant_name: p?.name || "",
          sku: p?.sku || "",
          purchase_price: p?.purchase_price || 0,
          retail_price: p?.retail_price || 0,
          attributes: {},
          inventory: [],
          customizations: { fields: [] },
        });

    const availableQty = getRowAvailableQty(productobj, selectedVariant);
    if (availableQty <= 0) {
      showError("This product is out of stock.");
      return;
    }

    const hasInv = selectedVariant && Array.isArray(selectedVariant.inventory) && selectedVariant.inventory.length > 0;
    if (selectedVariant && !hasInv) {
      showError("Selected variant has no inventory. Please choose an option with available stock.");
      return;
    }

    const incomingPid = String(productobj?.product_id ?? "").trim();
    const incomingVid = String(selectedVariant?.variant_id ?? "").trim();
    const lineKey = incomingPid && incomingVid ? `${incomingPid}:${incomingVid}` : "";
    // if (lineKey && (existingLineKeys || []).includes(lineKey)) {
    //   showError("This product is already added.");
    //   return;
    // }

    const invList: any[] = Array.isArray(selectedVariant?.inventory)
      ? selectedVariant.inventory
      : [];
    const inv = invList.find((e) => (e?.available ?? 0) > 0) || invList[0] || {};

    const templateFields: CustomField[] = getCustomizationFields(productobj, rowId);
    const values = rowCustomValues[rowId] || {};

    const fields = await Promise.all(
      templateFields.map(async (f) => {
        const raw = values[f.field_name];

        if (f.field_type === "file") {
          let valueId = "";

          if (raw instanceof File) {
            // ✅ upload now and store the returned id
            const [id] = await uploadFilesAndGetIds([raw], token || null);
            valueId = id || "";
          } else if (typeof raw === "string") {
            // ✅ already an id string from earlier selection
            valueId = raw.trim();
          }

          return {
            _id: f._id || "000000000000000000000000",
            field_name: f.field_name,
            field_type: f.field_type,
            value: valueId, // <-- ensure id string in payload
          };
        }

        // text (or other non-file) fields
        return {
          _id: f._id || "000000000000000000000000",
          field_name: f.field_name,
          field_type: f.field_type,
          value: typeof raw === "string" ? raw : "",
        };
      })
    );
    const taxRule = p?.tax_rule || null;
    const taxGroup = p?.tax_group || null;

    // choose raw dims exactly (variant → product)
    const vDims = (selectedVariant as any)?.package_dimensions;
    const pDims = (p as any)?.package_dimensions;
    const rawDims = vDims ?? pDims;

    const validDims = (d: any) =>
      d &&
      Number.isFinite(d.length) && d.length > 0 &&
      Number.isFinite(d.width) && d.width > 0 &&
      Number.isFinite(d.height) && d.height > 0 &&
      Number.isFinite(d.weight) && d.weight > 0;

    const package_dimensions = validDims(rawDims)
      ? {
        length: rawDims.length,
        width: rawDims.width,
        height: rawDims.height,
        weight: rawDims.weight,
        ...(rawDims.weight_type ? { weight_type: rawDims.weight_type } : {})
      }
      : undefined; // no defaults and no conversion

    const batchStudioKeyPayload =
      String(productobj?.product_id ?? "").trim() &&
      String(selectedVariant?.variant_id ?? "").trim()
        ? studioBatchVariantKey(
            String(productobj.product_id),
            String(selectedVariant.variant_id)
          )
        : "";
    const batchStudioEntry = batchStudioKeyPayload
      ? studioBatchVariantPreviews?.[batchStudioKeyPayload]
      : undefined;

    const payload = {
      product_id: productobj?.product_id || "",
      variant_id: selectedVariant?.variant_id || "",
      storage_location: {
        warehouse_id: inv?.warehouse_id || "",
        warehouse: inv?.warehouse_name || "",
        zone_id: inv?.zone_id || "",
        zone: inv?.zone_name || "",
        rack_id: inv?.rack_id || "",
        rack: inv?.rack_name || "",
        bin_id: inv?.bin_id || "",
        bin: inv?.bin_name || "",
      },
      name: selectedVariant?.variant_name || p?.name || "",
      sku: selectedVariant?.sku || p?.sku || "",
      unit_purchase_price: Number(
        selectedVariant?.purchase_price ?? p?.purchase_price ?? 0
      ),
      unit_retail_price: Number(
        selectedVariant?.retail_price ?? p?.retail_price ?? 0
      ),
      quantity: 1,
      tax_details: {
        tax_rate: taxRule?.taxRate ?? 0,
        tax_amount: 0,
        applied_state: taxRule?.state ?? "",
      },
      tax_amount: 0,
      tax_rule: taxRule || {},
      tax_group: taxGroup || {},
      ...(typeof p?.is_taxable === "boolean" ? { is_taxable: p.is_taxable } : {}),
      is_production: !!p?.is_production,
      images: productobj?.product?.images || [],
      amount: 0,
      customizations: {
        _id: "000000000000000000000000",
        template_name: selectedVariant?.variant_name || p?.name || "",
        fields,
      },
      image_url:
        (Array.isArray(p?.images) && p.images.length > 0 && p.images[0]?.url) ||
        p?.image?.url ||
        "",
      ...(package_dimensions ? { package_dimensions } : {}), // include only if valid
      ...(batchStudioEntry?.designId
        ? {
            design_id: batchStudioEntry.designId,
            design_name: batchStudioEntry.designName || "",
            preview_url: batchStudioEntry.previewFileId || "",
          }
        : {}),
    };

    console.log(payload, "imageload")

    onAdd(payload);
    setTempUploadIds(prev => ({ ...prev, [rowId]: [] }));
    setExpandedRowId(null);
  };

  // Seed attribute maps for new rows only — do not overwrite user variant picks.
  useEffect(() => {
    if (!Array.isArray(supplierProducts)) return;

    setAvailableAttributesByRow((prevAvail) => {
      const nextAvail = { ...prevAvail };
      supplierProducts.forEach((productobj, idx) => {
        const rowId = productobj?.product_id || String(idx);
        const p = productobj?.product || {};
        const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
        nextAvail[rowId] = buildAvailableAttributes(variants);
      });
      return nextAvail;
    });

    setSelectedVariantByRow((prev) => {
      const next = { ...prev };
      supplierProducts.forEach((productobj, idx) => {
        const rowId = productobj?.product_id || String(idx);
        if (next[rowId]) return;
        const p = productobj?.product || {};
        const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
        next[rowId] = firstVariantWithInventory(variants) ?? variants[0] ?? null;
      });
      return next;
    });

    setSelectedAttributesByRow((prev) => {
      const next = { ...prev };
      supplierProducts.forEach((productobj, idx) => {
        const rowId = productobj?.product_id || String(idx);
        const existing = next[rowId];
        if (existing && Object.keys(existing).some((k) => String(existing[k] ?? "").trim() !== "")) {
          return;
        }
        const p = productobj?.product || {};
        const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
        const avail = buildAvailableAttributes(variants);
        const def = firstVariantWithInventory(variants) ?? variants[0] ?? null;
        const sel: Record<string, string> = {};
        if (def?.attributes && Object.keys(def.attributes).length) {
          Object.entries(def.attributes).forEach(([k, v]) => (sel[k] = String(v)));
        } else {
          Object.keys(avail).forEach((k) => {
            const arr = avail[k] || [];
            if (arr.length) sel[k] = arr[0];
          });
        }
        next[rowId] = sel;
      });
      return next;
    });
  }, [supplierProducts]);

  const hasAnyCustomizationFields = (productObj: ProductObj) => {
    const p = productObj?.product || {};
    const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
    const variantFieldsCount = variants.reduce((count, v) => {
      const arr = Array.isArray(v?.customizations?.fields)
        ? v.customizations.fields
        : [];
      return count + arr.length;
    }, 0);
    const productFieldsCount = Array.isArray(productObj?.customizations?.fields)
      ? productObj.customizations.fields.length
      : 0;
    return variantFieldsCount + productFieldsCount > 0;
  };

  // columns — UI tuned to match your reference
  const columns = useMemo<ColumnDef<ProductObj, any>[]>(() => [
    columnHelper.display({
      id: "image",
      header: () => <div className="table-headers text-center">Image</div>,
      cell: (info) => {
        const productobj = info.row.original as ProductObj;
        const rowId = productobj?.product_id || String(info.row.index);
        const product = productobj?.product;
        const resolvedForThumb = resolveVariantForCustomizeRow(
          productobj,
          rowId,
          selectedVariantByRow,
          selectedAttributesByRow
        );
        const defaultImageId =
          product?.image?.url ||
          (product?.images?.length ? product.images[0].url : "");
        const batchKey =
          productobj?.product_id && resolvedForThumb?.variant_id
            ? studioBatchVariantKey(
                String(productobj.product_id),
                String(resolvedForThumb.variant_id)
              )
            : "";
        const batchPreviewId =
          batchKey && studioBatchVariantPreviews?.[batchKey]?.previewFileId
            ? String(studioBatchVariantPreviews[batchKey].previewFileId).trim()
            : "";
        const imageId =
          batchPreviewId && isCloudFileId(batchPreviewId) ? batchPreviewId : defaultImageId;

        const url =
          getImageSrc(imageId, imageUrlMap) ||
          (imageId?.startsWith("http") ? imageId : (imageId ? `${process.env.REACT_APP_BASE_URL}${imageId}` : ""));

        return (
          <div className="table-cells text-center">
            {url ? (
              <img
                src={url}
                alt="Product"
                style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 8 }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = imageCustomSquare;
                }}
              />
            ) : (
              <img
                src={imageCustomSquare}
                alt="No image"
                style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 8 }}
              />
            )}
          </div>
        );

        // const product = info.row.original?.product;
        // const imageUrl =
        //   product?.image?.url ||
        //   (product?.images?.length ? product.images[0].url : "");
        // const url = imageUrl ? `${process.env.REACT_APP_BASE_URL}${imageUrl}` : "";
        // return (
        //   <div className="table-cells text-center">
        //     {imageUrl ? (
        //       <img
        //         src={url}
        //         alt="Product"
        //         style={{
        //           width: 48,
        //           height: 48,
        //           objectFit: "cover",
        //           borderRadius: 8,
        //         }}
        //       />
        //     ) : (
        //       <span className="text-muted">No Image</span>
        //     )}
        //   </div>
        // );
      },
      enableGlobalFilter: false,
    }),

    // Product name + SKU stacked (left-aligned)
    columnHelper.accessor((row: ProductObj) => row?.product?.name || "-", {
      id: "name",
      header: () => <div className="table-headers">Product Name</div>,
      cell: (info) => {
        const productobj = info.row.original as ProductObj;
        const p = productobj?.product || {};
        const rowId = productobj?.product_id || String(info.row.index);
        const selectedVariant = selectedVariantByRow?.[rowId];
        return (
          <div className="title-sm">
            <div className="text-dark wrap-anywhere" style={{ lineHeight: "25px" }}>
              {selectedVariant?.variant_name || p?.name || "-"}
            </div>
            <div className="text-dark fs-13 fw-medium" style={{ marginTop: "10px" }}>SKU: {selectedVariant?.sku || p?.sku || "-"}</div>
          </div>
        );
      },
      enableGlobalFilter: true,
    }),

    columnHelper.display({
      id: "avl_qty",
      header: () => <div className="table-headers text-center">Avl Qty</div>,
      cell: (info) => {
        const productobj = info.row.original as ProductObj;
        const rowId = productobj?.product_id || String(info.row.index);
        const selectedVariant = selectedVariantByRow?.[rowId];
        const qty = getRowAvailableQty(productobj, selectedVariant);
        return (
          <div className="table-cells text-center">
            <span
              style={{
                fontWeight: 600,
                color: qty > 0 ? "#334155" : "#ef4444",
                fontSize: 14,
              }}
            >
              {qty.toLocaleString()}
            </span>
          </div>
        );
      },
    }),

    // Category
    columnHelper.accessor((row) => row?.product?.category || "-", {
      id: "category",
      header: () => <div className="table-headers ">Category</div>,
      cell: (info) => (
        <div className="ins-category-badge">{info.getValue()}</div>
      ),
    }),

    // Attributes (compact selects)
    columnHelper.display({
      id: "attributes",
      header: () => <div className="table-headers text-center">Variants</div>,
      cell: (info) => {
        const productobj = info.row.original as ProductObj;
        const rowId = productobj?.product_id || String(info.row.index);

        let availableAttributes = availableAttributesByRow[rowId];
        if (!availableAttributes || Object.keys(availableAttributes).length === 0) {
          const p = productobj?.product || {};
          const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
          availableAttributes = buildAvailableAttributes(variants);
          if (Object.keys(availableAttributes).length > 0) {
            setAvailableAttributesByRow((prev) => ({
              ...prev,
              [rowId]: availableAttributes!,
            }));
          }
        }

        const selectedAttributes = selectedAttributesByRow[rowId] || {};
        const keys = Object.keys(availableAttributes || {});
        if (keys.length === 0) return <span className="text-muted">—</span>;
        const p = productobj?.product || {};
        const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
        const optsWithInventory = getAttributeOptionsWithInventory(variants, availableAttributes || {});
        return (
          <div className="table-cells">
            <div className="d-flex flex-column gap-2">
              {keys.map((attrKey) => {
                const opts = optsWithInventory[attrKey] || (availableAttributes?.[attrKey] || []).map((v: string) => ({ value: v, label: v, isDisabled: false }));
                const current = selectedAttributes[attrKey]
                  ? { value: selectedAttributes[attrKey], label: selectedAttributes[attrKey] }
                  : null;

                return (
                  <div key={attrKey}>
                    <div className="ins-attr-label">
                      {String(attrKey || "").replace(/_/g, " ")}
                    </div>

                    <OrderCheckoutVariantSelect
                      name={`${rowId}-${attrKey}`}
                      value={current}
                      options={opts}
                      placeholder="- Select -"
                      onChange={(opt) => handleAttributeChange(productobj, rowId, attrKey, opt?.value ?? "")}
                      className="attr-select"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );



        // return (
        //   <div className="table-cells">
        //     <div className="d-flex flex-column gap-2">
        //       {keys.map((attrKey) => (
        //         <div key={attrKey}>
        //           <div className="text-muted xsmall mb-1">{attrKey}</div>
        //           <select
        //             className="form-select form-select-sm"
        //             value={selectedAttributes[attrKey] ?? ""}
        //             onChange={(e) =>
        //               handleAttributeChange(productobj, rowId, attrKey, e.target.value)
        //             }
        //           >
        //             <option value="" disabled>— Select —</option>
        //             {(availableAttributes?.[attrKey] || []).map((val: string) => (
        //               <option key={val} value={val}>
        //                 {val}
        //               </option>
        //             ))}
        //           </select>
        //         </div>
        //       ))}
        //     </div>
        //   </div>
        // );
      },
      enableGlobalFilter: false,
    }),

    // Custom (only if any fields exist)
    columnHelper.display({
      id: "custom",
      header: () => <div className="table-headers text-center">Customize</div>,
      cell: (info) => {
        const productObj = info.row.original as ProductObj;
        const rowId = getRowId(productObj, info.row.index);
        const showCustomize = hasAnyCustomizationFields(productObj);
        const hasRequiredCustomizationField = showCustomize
          ? getCustomizationFields(productObj, rowId).some((field) => Boolean(field?.required))
          : false;
        const expanded = expandedRowId === rowId;

        const resolvedVariant = resolveVariantForCustomizeRow(
          productObj,
          rowId,
          selectedVariantByRow,
          selectedAttributesByRow
        );
        const templateIdRaw = (resolvedVariant as { template_id?: string })?.template_id;
        const hasStudioTemplate =
          typeof templateIdRaw === "string" && templateIdRaw.trim().length > 0;
        const variantIdForStudio = String(
          (resolvedVariant as { variant_id?: string } | undefined)?.variant_id ?? ""
        ).trim();
        const hasExistingBatchDesign = isBatchStudioDesignForRow(productObj, rowId);

        if (!showCustomize && !hasStudioTemplate) {
          return (
            <div className="table-cells text-center">
              <span className="text-muted">—</span>
            </div>
          );
        }

        return (
          <div className="table-cells text-center">
            <div className="d-inline-flex flex-column gap-2 align-items-center">
              {showCustomize && (
                <div style={{ position: "relative", display: "inline-flex" }}>
                  {hasRequiredCustomizationField ? (
                    <span
                      className="text-danger"
                      style={{
                        position: "absolute",
                        top: '12px',
                        left: '-12px',
                        fontSize: '1.375rem',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      *
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-light btn-sm border br-8 d-inline-flex align-items-center gap-2"
                    onClick={() => openConfigurator(productObj, rowId)}
                  >
                    {expanded ? (
                      <>
                        <i className={`ri-arrow-${expanded ? "up" : "down"}-s-line fs-17`} />
                        <span className="fs-13">Customize</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-arrow-down-s-line fs-17" />
                        <span className="fs-13">Customize</span>
                      </>
                    )}
                  </button>
                </div>
              )}
              {hasStudioTemplate && (
                <button
                  type="button"
                  disabled={studioInstantiateRowId === rowId || !supplierId}
                  onClick={() =>
                    openCustomizeInStudio(
                      templateIdRaw.trim(),
                      rowId,
                      String(productObj.product_id ?? ""),
                      variantIdForStudio
                    )
                  }
                  style={{
                    background: "#eef2ff",
                    border: "1px solid #c7d2fe",
                    borderRadius: 8,
                    cursor: studioInstantiateRowId === rowId ? "wait" : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#3730a3",
                    padding: "5px 10px",
                    transition: "all 0.2s",
                    opacity: studioInstantiateRowId === rowId ? 0.75 : 1,
                  }}
                >
                  {studioInstantiateRowId === rowId
                    ? "Opening…"
                    : hasExistingBatchDesign
                      ? "Edit design"
                      : "Customize in studio"}
                </button>
              )}
            </div>
          </div>
        );
      },
    }),

    // Actions
    columnHelper.display({
      id: "actions",
      header: () => <div className="table-headers text-center">Actions</div>,
      cell: (info) => {
        const productObj = info.row.original;
        const p = productObj?.product || {};
        const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
        const hasAttributes = variants.some((v) => v?.attributes && Object.keys(v.attributes).length > 0);
        const hasCustomizations =
          variants.some((v) => Array.isArray(v?.customizations?.fields) && v.customizations.fields.length > 0) ||
          (Array.isArray(productObj?.customizations?.fields) && productObj.customizations.fields.length > 0);
        const hasConfig = hasCustomizations;

        const rowId = getRowId(productObj, info.row.index);
        const hasRequiredCustomization = hasConfig
          ? getCustomizationFields(productObj, rowId).some((field) => Boolean(field?.required))
          : false;
        const canSubmitThisRow = hasConfig
          ? isBatchStudioDesignForRow(productObj, rowId) ||
            !hasRequiredCustomization ||
            !!rowValidity[rowId]
          : true;
        const selectedVariant = selectedVariantByRow?.[rowId];
        const outOfStock = getRowAvailableQty(productObj, selectedVariant) <= 0;

        return (
          <div className="table-cells text-center">
            <button
              type="button"
              className="pp-add-custom-btn"
              disabled={!canSubmitThisRow || outOfStock}
              onClick={() => handleAddConfigured(productObj)}
            >
              Add
            </button>
          </div>
        );
      },
    }),
    //   columnHelper.display({
    //     id: "actions",
    //     header: () => <div className="table-headers text-center w-100">Actions</div>,
    //     cell: (info) => {
    //       const productObj = info.row.original;
    //       const p = productObj?.product || {};
    //       const variants: Variant[] = Array.isArray(p?.variants) ? p.variants : [];
    //       const hasAttributes = variants.some((v) => v?.attributes && Object.keys(v.attributes).length > 0);
    //       const hasCustomizations =
    // variants.some((v) => Array.isArray(v?.customizations?.fields) && v.customizations.fields.length > 0) ||
    // (Array.isArray(productObj?.customizations?.fields) && productObj.customizations.fields.length > 0);
    //       const hasConfig = hasCustomizations;

    //       const rowId = getRowId(productObj, info.row.index);
    //       const canSubmitThisRow = hasConfig ? expandedRowId === rowId && !!rowValidity[rowId] : true;
    //       return (
    //         <div className="table-cells text-center w-100"  style={{
    //   height: "30px", // ✅ shorter height
    //   padding: "2px 6px", // ✅ less padding for compact look
    //   fontSize: "0.85rem", // optional: smaller font
    //   borderRadius: "6px",
    // }}>
    //           <button
    //             type="button"
    //             className="btn btn-primary btn-sm br-8"
    //             disabled={!canSubmitThisRow}
    //             onClick={() => handleAddConfigured(productObj)}
    //           >
    //             Add Product
    //           </button>
    //         </div>
    //       );
    //     },
    //   }),
  ], [
    availableAttributesByRow,
    selectedAttributesByRow,
    selectedVariantByRow,
    expandedRowId,
    rowValidity,
    imageUrlMap,
    supplierId,
    studioInstantiateRowId,
    openCustomizeInStudio,
    studioCustomization,
    studioBatchVariantPreviews,
    isBatchStudioDesignForRow,
  ]);

  const table = useReactTable({
    data: supplierProducts,
    columns,
    pageCount: Math.max(1, Math.ceil(rowCount / pagination.pageSize)),
    state: { pagination, globalFilter },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });


  useEffect(() => {
    if (!open) {
      const toDelete = Object.values(tempUploadIds).flat();
      if (toDelete.length) {
        hardDeletePresignedFiles(toDelete, token).catch(() => { });
      }
      setTempUploadIds({});
      setUploaded([]); // optional: clear the in-session list
    }
  }, [open, token]);


  const resolvePreviewSrc = (idOrUrl?: string): string | "" => {
    if (!idOrUrl) return "";
    // NEW: allow local previews
    if (idOrUrl.startsWith("blob:") || idOrUrl.startsWith("data:")) return idOrUrl;

    // existing behavior for ids/urls
    return (
      getImageSrc(idOrUrl, imageUrlMap) ||
      (idOrUrl.startsWith("http") ? idOrUrl : `${process.env.REACT_APP_BASE_URL}${idOrUrl}`)
    );
  };



  if (!open) return null;
  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,.52)" }}
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-xxl modal-dialog-scrollable mt-9" id="SalesOrder-popup">
        <div className="modal-content overflow-hidden" id="checkoutpopup">
          {/* ================= HEADER (HTML STYLE) ================= */}
          <div className="pp-modal-header">
            <div className="pp-header-top">
              <h2 className="pp-modal-title">Select a product</h2>

              <button
                type="button"
                className="pp-close-btn"
                onClick={onClose}
                aria-label="Close"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="pp-header-search">
              <div className="pp-search-box">
                <i className="ri-search-line pp-search-icon" />
                <input
                  type="text"
                  className="pp-search-input"
                  placeholder="Search by product name, SKU, or category..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                      setGlobalFilter(searchInput);
                    }
                  }}
                />
              </div>
            </div>

            <div className="text-center text-muted" style={{ fontSize: 13 }}>
              Showing {rowCount || 0} products
            </div>
          </div>

          {/* ================= BODY ================= */}
          <div className="modal-body p-0">
            {loading && <div className="text-center py-5">Loading…</div>}
            {error && <div className="text-danger p-4">{error}</div>}

            {!loading && !error && (
              <>
                {/* Desktop View (≥1022px) */}
                <div className="rdt-desktop">
                  <div className="pp-table-container">
                    <table
                      className="pp-table"
                      style={{ tableLayout: "fixed", width: "100%", minWidth: "1022px" }}
                    >
                      <colgroup>
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "25%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "10%" }} />
                      </colgroup>

                      <thead>
                        {table.getHeaderGroups().map((hg) => (
                          <tr key={hg.id}>
                            {hg.headers.map((header) => (
                              <th key={header.id}>
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>

                      <tbody>
                        {table.getRowModel().rows.map((row) => {
                          const productobj = row.original as ProductObj;
                          const p = productobj?.product;
                          const variants: Variant[] = p?.variants || [];
                          const rowId = getRowId(productobj, row.index);
                          const isExpanded = expandedRowId === rowId;

                          const hasAttributes = variants.some(
                            (v) => v?.attributes && Object.keys(v.attributes).length > 0
                          );

                          const customizationFields: CustomField[] = (() => {
                            const selectedVariant = selectedVariantByRow[rowId];
                            if (selectedVariant?.customizations?.fields?.length)
                              return selectedVariant.customizations.fields;
                            if (variants[0]?.customizations?.fields?.length)
                              return variants[0].customizations!.fields;
                            if (productobj?.customizations?.fields?.length)
                              return productobj.customizations!.fields!;
                            return [];
                          })();

                          const customValues = rowCustomValues[rowId] || {};
                          const filePreviews = rowFilePreviews[rowId] || {};

                          const needsCustomizationPanel =
                            customizationFields.length > 0; // ✅ show panel when fields exist

                          return (
                            <React.Fragment key={row.id}>
                              {/* main row */}
                              <tr className={isExpanded ? "pp-row-expanded" : ""}>
                                {row.getVisibleCells().map((cell) => (
                                  <td key={cell.id}>
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )}
                                  </td>
                                ))}
                              </tr>

                              {/* expanded customization panel */}
                              {isExpanded && needsCustomizationPanel && (
                                <tr className="pp-customization-row">
                                  <td colSpan={row.getVisibleCells().length}>
                                    <div className="pp-customization-panel">
                                      <div className="pp-customization-title">
                                        <i className="ri-edit-2-line" />
                                        Product customization
                                      </div>

                                      {/* 2-column form like HTML */}
                                      <div className="pp-customization-form">
                                        {customizationFields.map((field) => {
                                          const key = field._id || field.field_name;

                                          const fileVal = customValues[field.field_name];
                                          const idOrUrl =
                                            filePreviews[field.field_name] ||
                                            (typeof fileVal === "string" ? fileVal : "");

                                          const previewSrc = resolvePreviewSrc(idOrUrl);

                                          const inputId = `file-${rowId}-${field.field_name}`;

                                          return (
                                            <div className="pp-form-group" key={key}>
                                              <label className="pp-form-label">
                                                {field.field_name}
                                                {field.required && (
                                                  <span className="text-danger ms-1">*</span>
                                                )}
                                              </label>

                                              {field.field_type === "text" ? (
                                                <input
                                                  type="text"
                                                  className="pp-form-input"
                                                  placeholder={`Enter ${field.field_name}`}
                                                  value={(customValues[field.field_name] as string) ?? ""}
                                                  onChange={(e) =>
                                                    handleTextChange(
                                                      productobj,
                                                      rowId,
                                                      field,
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                              ) : (
                                                <>
                                                  {/* ✅ HTML style file input */}
                                                  <div className="pp-file-wrapper">
                                                    <div
                                                      className="pp-file-display"
                                                      role="button"
                                                      tabIndex={0}
                                                      onClick={() => {
                                                        const el = document.getElementById(
                                                          inputId
                                                        ) as HTMLInputElement | null;
                                                        el?.click();
                                                      }}
                                                      onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                          const el = document.getElementById(
                                                            inputId
                                                          ) as HTMLInputElement | null;
                                                          el?.click();
                                                        }
                                                      }}
                                                    >
                                                      <span className="pp-file-name">
                                                        {(() => {
                                                          // show file name if user selected (File object stored?) else show default
                                                          const v = customValues[field.field_name];
                                                          if (v && typeof v === "string") return v;
                                                          return "No file chosen";
                                                        })()}
                                                      </span>

                                                      <i className="ri-upload-2-line" />
                                                    </div>

                                                    <input
                                                      id={inputId}
                                                      type="file"
                                                      accept="image/*"
                                                      className="pp-file-hidden"
                                                      onChange={(e) => {
                                                        const file = e.currentTarget.files?.[0] || null;
                                                        handleFileChange(productobj, rowId, field, file);
                                                      }}
                                                    />
                                                  </div>

                                                  {/* Optional preview */}
                                                  {previewSrc && (
                                                    <div className="pp-preview">
                                                      <img src={previewSrc} alt="Preview" />
                                                      <button
                                                        type="button"
                                                        className="pp-preview-remove"
                                                        title="Remove"
                                                        onClick={() =>
                                                          handleRemoveCustomFile(productobj, rowId, field)
                                                        }
                                                      >
                                                        <i className="ri-close-line" />
                                                      </button>
                                                    </div>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>

                                      {/* Actions (right side like HTML) */}
                                      <div className="pp-customization-actions">
                                        <button
                                          type="button"
                                          className="pp-cancel-btn"
                                          onClick={() => setExpandedRowId(null)}
                                        >
                                          Cancel
                                        </button>

                                        <button
                                          type="button"
                                          className="pp-add-custom-btn"
                                          disabled={
                                            getRowAvailableQty(productobj, selectedVariantByRow?.[rowId]) <= 0 ||
                                            (getCustomizationFields(productobj, rowId).some((field) => Boolean(field?.required)) &&
                                            !rowValidity[rowId] &&
                                            !isBatchStudioDesignForRow(productobj, rowId))
                                          }
                                          onClick={() => handleAddConfigured(productobj)}
                                        >
                                          Add
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}

                        {table.getRowModel().rows.length === 0 && (
                          <tr>
                            <td
                              className="text-center py-4 text-muted"
                              colSpan={table.getAllLeafColumns().length}
                            >
                              No products found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile View (≤1021px) */}
                <div className="rdt-mobile">
                  {/* keep your existing mobile code exactly as it is */}
                  <div className="product-picker-wrap" style={{ maxHeight: 480, overflowY: "auto" }}>
                    {table.getRowModel().rows.length === 0 ? (
                      <div className="text-center py-4 text-muted">No products found.</div>
                    ) : (
                      <div className="d-flex flex-column gap-3 p-2">
                        {table.getRowModel().rows.map((row) => {
                          const productobj = row.original as ProductObj;
                          const p = productobj?.product;
                          const variants: Variant[] = p?.variants || [];
                          const rowId = getRowId(productobj, row.index);
                          const isExpanded = expandedRowId === rowId;

                          const customizationFields: CustomField[] = (() => {
                            const selectedVariant = selectedVariantByRow[rowId];
                            if (selectedVariant?.customizations?.fields?.length)
                              return selectedVariant.customizations.fields;
                            if (variants[0]?.customizations?.fields?.length)
                              return variants[0].customizations!.fields;
                            if (productobj?.customizations?.fields?.length)
                              return productobj.customizations!.fields!;
                            return [];
                          })();

                          const customValues = rowCustomValues[rowId] || {};
                          const filePreviews = rowFilePreviews[rowId] || {};

                          const cells = row.getVisibleCells();
                          const imageCell = cells.find((c) => c.column.id === "image");
                          const nameCell = cells.find((c) => c.column.id === "name");
                          const avlQtyCell = cells.find((c) => c.column.id === "avl_qty");
                          const categoryCell = cells.find((c) => c.column.id === "category");
                          const attributesCell = cells.find((c) => c.column.id === "attributes");
                          const customCell = cells.find((c) => c.column.id === "custom");
                          const actionsCell = cells.find((c) => c.column.id === "actions");

                          return (
                            <div key={row.id} className="card border">
                              <div className="card-body">
                                <div className="d-flex align-items-start gap-3 mb-3">
                                  {imageCell && (
                                    <div style={{ flexShrink: 0 }}>
                                      {flexRender(imageCell.column.columnDef.cell, imageCell.getContext())}
                                    </div>
                                  )}

                                  <div className="flex-grow-1">
                                    {nameCell && (
                                      <div className="mb-2">
                                        {flexRender(nameCell.column.columnDef.cell, nameCell.getContext())}
                                      </div>
                                    )}
                                    {avlQtyCell && (
                                      <div className="mb-2">
                                        <div className="text-muted small mb-1">Avl Qty</div>
                                        {flexRender(avlQtyCell.column.columnDef.cell, avlQtyCell.getContext())}
                                      </div>
                                    )}
                                  </div>

                                  {actionsCell && (
                                    <div style={{ flexShrink: 0 }}>
                                      {flexRender(actionsCell.column.columnDef.cell, actionsCell.getContext())}
                                    </div>
                                  )}
                                </div>

                                {categoryCell && (
                                  <div className="mb-2">
                                    <div className="text-muted small mb-1">Category</div>
                                    <div className="ins-category-badge">{flexRender(categoryCell.column.columnDef.cell, categoryCell.getContext())}</div>
                                  </div>
                                )}

                                {attributesCell && (
                                  <div className="mb-2">
                                    <div className="text-muted small mb-1">Attributes</div>

                                    {/* ✅ add this wrapper */}
                                    <div className="attr-titlecase">
                                      {flexRender(attributesCell.column.columnDef.cell, attributesCell.getContext())}
                                    </div>
                                  </div>
                                )}


                                {customCell && (
                                  <div className="mb-2">
                                    {flexRender(customCell.column.columnDef.cell, customCell.getContext())}
                                  </div>
                                )}

                                {/* mobile customization panel (same style) */}
                                {isExpanded && customizationFields.length > 0 && (
                                  <div className="mt-3 pt-3 border-top">
                                    <div className="pp-customization-panel">
                                      <div className="pp-customization-title">
                                        <i className="ri-edit-2-line" />
                                        Product customization
                                      </div>

                                      <div className="pp-customization-form pp-customization-form-mobile">
                                        {customizationFields.map((field) => {
                                          const key = field._id || field.field_name;
                                          const fileVal = customValues[field.field_name];
                                          const idOrUrl =
                                            filePreviews[field.field_name] ||
                                            (typeof fileVal === "string" ? fileVal : "");
                                          const previewSrc = resolvePreviewSrc(idOrUrl);
                                          const inputId = `mfile-${rowId}-${field.field_name}`;

                                          return (
                                            <div className="pp-form-group" key={key}>
                                              <label className="pp-form-label">
                                                {field.field_name}
                                                {field.required && (
                                                  <span className="text-danger ms-1">*</span>
                                                )}
                                              </label>

                                              {field.field_type === "text" ? (
                                                <input
                                                  type="text"
                                                  className="pp-form-input"
                                                  placeholder={`Enter ${field.field_name}`}
                                                  value={(customValues[field.field_name] as string) ?? ""}
                                                  onChange={(e) =>
                                                    handleTextChange(productobj, rowId, field, e.target.value)
                                                  }
                                                />
                                              ) : (
                                                <>
                                                  <div className="pp-file-wrapper">
                                                    <div
                                                      className="pp-file-display"
                                                      role="button"
                                                      tabIndex={0}
                                                      onClick={() => {
                                                        const el = document.getElementById(inputId) as HTMLInputElement | null;
                                                        el?.click();
                                                      }}
                                                    >
                                                      <span className="pp-file-name">
                                                        {(() => {
                                                          const v = customValues[field.field_name];
                                                          if (v && typeof v === "string") return v;
                                                          return "No file chosen";
                                                        })()}
                                                      </span>
                                                      <i className="ri-upload-2-line" />
                                                    </div>

                                                    <input
                                                      id={inputId}
                                                      type="file"
                                                      accept="image/*"
                                                      className="pp-file-hidden"
                                                      onChange={(e) => {
                                                        const file = e.currentTarget.files?.[0] || null;
                                                        handleFileChange(productobj, rowId, field, file);
                                                      }}
                                                    />
                                                  </div>

                                                  {previewSrc && (
                                                    <div className="pp-preview">
                                                      <img src={previewSrc} alt="Preview" />
                                                      <button
                                                        type="button"
                                                        className="pp-preview-remove"
                                                        title="Remove"
                                                        onClick={() => handleRemoveCustomFile(productobj, rowId, field)}
                                                      >
                                                        <i className="ri-close-line" />
                                                      </button>
                                                    </div>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>

                                      <div className="pp-customization-actions">
                                        <button
                                          type="button"
                                          className="pp-cancel-btn"
                                          onClick={() => setExpandedRowId(null)}
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          type="button"
                                          className="pp-add-custom-btn"
                                          disabled={
                                            getRowAvailableQty(productobj, selectedVariantByRow?.[rowId]) <= 0 ||
                                            (getCustomizationFields(productobj, rowId).some((field) => Boolean(field?.required)) &&
                                            !rowValidity[rowId] &&
                                            !isBatchStudioDesignForRow(productobj, rowId))
                                          }
                                          onClick={() => handleAddConfigured(productobj)}
                                        >
                                          Add
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* footer pagination */}
          {table.getRowModel().rows.length === 0 ? (
            <div className="text-center my-4 text-muted" style={{ fontSize: "1.05rem" }}>
              No records found
            </div>
          ) : (
            <div className="px-4 pb-4 pt-2 border-top bg-white">
              <PaginationControls table={table} />
            </div>
          )}
        </div>
      </div>

      {/* ================= STYLES (HTML MATCH) ================= */}
      <style>{`
      #checkoutpopup {
        border-radius: 12px !important;
        box-shadow: 0 24px 48px rgba(0,0,0,0.2);
      }

      /* Header */
      .pp-modal-header{
        padding: 28px 32px;
        border-bottom: 1px solid #e5e7eb;
        background: linear-gradient(to bottom, #ffffff, #fafbfc);
        display:flex;
        flex-direction:column;
        gap: 18px;
      }
      .pp-header-top{
        display:flex;
        align-items:center;
        justify-content:center;
        position:relative;
      }
      .pp-modal-title{
        font-size:22px;
        font-weight:600;
        color:#111827;
        letter-spacing:-0.02em;
        margin:0;
      }
      .pp-close-btn{
        position:absolute;
        right:0;
        width:36px;
        height:36px;
        border-radius:8px;
        border:none;
        background:#f3f4f6;
        color:#6b7280;
        cursor:pointer;
        display:flex;
        align-items:center;
        justify-content:center;
        transition:all .2s;
      }
      .pp-close-btn:hover{
        background:#fee2e2;
        color:#dc2626;
        transform:scale(1.05);
      }

      .pp-header-search{
        display:flex;
        justify-content:center;
      }
      .pp-search-box{
        position:relative;
        width:100%;
        max-width:560px;
      }
      .pp-search-icon{
        position:absolute;
        left:16px;
        top:50%;
        transform:translateY(-50%);
        color:#9ca3af;
        pointer-events:none;
        font-size:18px;
      }
      .pp-search-input{
        width:100%;
        padding:12px 20px 12px 46px;
        border:1.5px solid #d1d5db;
        border-radius:10px;
        font-size:15px;
        transition:all .2s;
        background:#fff;
      }
      .pp-search-input:focus{
        outline:none;
        border-color:#3b82f6;
        box-shadow:0 0 0 4px rgba(59,130,246,0.1);
      }
      .pp-search-input::placeholder{ color:#9ca3af; }

      /* Table */
      .pp-table-container{
        flex:1;
        max-height: 520px;
        overflow-y:auto;
        overflow-x:auto;
        position: relative;
      }
      /* Stacking for open menu: elevate only the select cell wrapper — not the whole <tr>
         (position on tr + transition:all caused sibling td/header jitter while scrolling). */
      .pp-table{
        width:100%;
        border-collapse:collapse;
      }
      .pp-table thead{
        position:sticky;
        top:0;
        z-index:10;
        background:#fff;
      }
      .pp-table thead tr{
        border-bottom:2px solid #e5e7eb;
      }
      .pp-table th{
        padding:16px 20px;
        text-align:left;
        font-size:13px;
        font-weight:600;
        color:#6b7280;
        text-transform:uppercase;
        letter-spacing:.05em;
        background:#f9fafb;
        white-space:nowrap;
      }
      .pp-table td{
        padding:24px 20px;
        font-size:14px;
        color:#374151;
        vertical-align:middle;
      }
      .pp-table tbody tr{
        border-bottom:1px solid #f3f4f6;
        transition:background-color .2s ease, box-shadow .2s ease;
      }
      .pp-table tbody tr:hover{
        background:#fafbfc;
        box-shadow:0 2px 8px rgba(0,0,0,0.04);
      }
      .pp-row-expanded{ background:#fafbfc; }
      .pp-customization-row td{
        padding: 28px 32px !important;
        background:#f9fafb;
      }

      /* Customization Panel (FIXED to match HTML) */
      .pp-customization-panel{
        background:#fff;
        border-radius:10px;
        padding:24px;
        border:1.5px solid #e5e7eb;
      }
      .pp-customization-title{
        font-size:16px;
        font-weight:600;
        color:#111827;
        margin-bottom:20px;
        display:flex;
        align-items:center;
        gap:8px;
      }
      .pp-customization-title i{
        font-size:18px;
        color:#6b7280;
      }
      .pp-customization-form{
        display:grid;
        grid-template-columns:repeat(2, 1fr);
        gap:20px;
        margin-bottom:24px;
      }
      .pp-customization-form-mobile{
        grid-template-columns:1fr;
      }
      .pp-form-group{
        display:flex;
        flex-direction:column;
        gap:8px;
      }
      .pp-form-label{
        font-size:13px;
        font-weight:500;
        color:#374151;
        display:flex;
        align-items:center;
        gap:4px;
        margin:0;
      }
      .pp-form-input{
        padding:10px 14px;
        border:1.5px solid #e5e7eb;
        border-radius:8px;
        font-size:14px;
        color:#374151;
        background:#fff;
        transition:all .2s;
      }
      .pp-form-input:hover{ border-color:#d1d5db; }
      .pp-form-input:focus{
        outline:none;
        border-color:#3b82f6;
        box-shadow:0 0 0 3px rgba(59,130,246,0.08);
      }

      /* file input (dashed, clickable) */
      .pp-file-wrapper{ position:relative; }
      .pp-file-display{
        padding:10px 14px;
        border:1.5px dashed #d1d5db;
        border-radius:8px;
        font-size:14px;
        color:#6b7280;
        background:#fafbfc;
        cursor:pointer;
        transition:all .2s;
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:12px;
      }
      .pp-file-display:hover{
        border-color:#3b82f6;
        background:#f0f9ff;
      }
      .pp-file-name{
        overflow:hidden;
        text-overflow:ellipsis;
        white-space:nowrap;
        max-width: 320px;
      }
      .pp-file-hidden{ display:none; }

      /* preview */
      .pp-preview{
        margin-top:10px;
        position:relative;
        width:120px;
      }
      .pp-preview img{
        width:120px;
        height:auto;
        border-radius:8px;
        display:block;
      }
      .pp-preview-remove{
        position:absolute;
        top:-10px;
        right:-10px;
        width:28px;
        height:28px;
        border-radius:999px;
        border:1px solid #e5e7eb;
        background:#fff;
        display:flex;
        align-items:center;
        justify-content:center;
        cursor:pointer;
      }
      .pp-preview-remove:hover{
        background:#fee2e2;
        border-color:#fecaca;
        color:#dc2626;
      }

      /* actions row */
      .pp-customization-actions{
        display:flex;
        gap:12px;
        justify-content:flex-end;
        padding-top:4px;
      }
      .pp-cancel-btn{
        padding:10px 24px;
        background:#fff;
        color:#dc2626;
        border:1.5px solid #dc2626;
        border-radius:8px;
        font-size:14px;
        font-weight:600;
        cursor:pointer;
        transition:all .2s;
      }
      .pp-cancel-btn:hover{
        background:#dc2626;
        color:#fff;
        transform:translateY(-1px);
        box-shadow:0 4px 12px rgba(220,38,38,0.25);
      }
      .pp-add-custom-btn{
        padding:10px 28px;
        background:linear-gradient(135deg,#3b82f6 0%,#2563eb 100%);
        color:#fff;
        border:none;
        border-radius:8px;
        font-size:14px;
        font-weight:600;
        cursor:pointer;
        transition:all .2s;
        box-shadow:0 2px 4px rgba(59,130,246,0.2);
      }
      .pp-add-custom-btn:hover:not(:disabled){
        background:linear-gradient(135deg,#2563eb 0%,#1d4ed8 100%);
        box-shadow:0 4px 12px rgba(59,130,246,0.35);
        transform:translateY(-2px);
      }
      .pp-add-custom-btn:disabled{
        opacity:0.6;
        cursor:not-allowed;
      }

      /* responsive visibility */
      .rdt-desktop { display:block; }
      .rdt-mobile { display:none; }
      @media (max-width:1021px){
        .rdt-desktop { display:none; }
        .rdt-mobile { display:block; }
      }
        /* make field labels like: style -> Style */
.attr-titlecase .attribute-label,
.attr-titlecase label,
.attr-titlecase .attr-name {
  text-transform: capitalize;
}
  .ins-category-badge{
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #166534;
  border: 1px solid #bbf7d0;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  letter-spacing: 0.01em;
  white-space: nowrap;
}

/* optional: if category comes lowercase */
.ins-category-badge{
  text-transform: capitalize;
}
  /* Attribute label like your screenshot */
.ins-attr-label{
  font-weight: 700;          /* bold */
  font-size: 15px;
  color: #6b7280;
  text-transform: capitalize; /* first letter caps (and each word) */
  letter-spacing: 0.02em;
  margin-bottom: 6px;
}

/* Variant column: react-select fills cell; long names truncate with ellipsis */
.attr-select {
  min-width: 0;
  width: 100%;
  max-width: 100%;
  position: relative;
}
#checkoutpopup .attr-select:has(.ocv__control--menu-is-open) {
  /* Below sticky thead (.pp-table thead { z-index: 10 }) so the menu does not paint over the header */
  z-index: 9 !important;
}
.attr-select .ocv__control {
  min-width: 0;
  width: 100% !important;
  overflow: hidden;
}
.attr-select .ocv__value-container {
  min-width: 0;
  flex-wrap: nowrap;
  overflow: hidden;
}
.attr-select .ocv__single-value,
.attr-select .ocv__single-value span {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.attr-select .ocv__menu {
  box-sizing: border-box;
}

.title-sm{
font-size: 1.05rem;
color: "#364153";
    font-family: "Roboto", sans-serif;
}


    `}</style>
    </div>
  );

  //   return (
  //     <div
  //       className="modal fade show"
  //       style={{ display: "block", backgroundColor: "rgba(0,0,0,.52)" }}
  //       aria-modal="true"
  //       role="dialog"
  //     >
  //       <div className=" modal-xxl modal-dialog-scrollable mt-9" id="SalesOrder-popup">
  //         <div className="modal-content rounded-4 overflow-hidden" id="checkoutpopup">
  //           {/* Header */}
  // {/* Header (like HTML) */}
  // <div className="pp-modal-header">
  //   <div className="pp-header-top">
  //     <h2 className="pp-modal-title">Select a product</h2>

  //     <button type="button" className="pp-close-btn" onClick={onClose} aria-label="Close">
  //       <i className="ri-close-line" />
  //     </button>
  //   </div>

  //   <div className="pp-header-search">
  //     <div className="pp-search-box">
  //       <i className="ri-search-line pp-search-icon" />
  //       <input
  //         type="text"
  //         className="pp-search-input"
  //         placeholder="Search by product name, SKU, or category..."
  //         value={searchInput}
  //         onChange={(e) => setSearchInput(e.target.value)}
  //         onKeyDown={(e) => {
  //           if (e.key === "Enter") setGlobalFilter(searchInput);
  //         }}
  //       />
  //     </div>
  //   </div>
  // </div>






  //           {/* Body */}
  //           <div className="modal-body p-0">
  //             {loading && <div className="text-center py-5">Loading…</div>}
  //             {error && <div className="text-danger p-4">{error}</div>}

  //             {!loading && !error && (
  //               <>
  //                 {/* Desktop View (≥1022px) */}
  //                 <div className="rdt-desktop">
  //                   <div className="pp-table-container">

  //                     <table
  //                       className="pp-table"
  //                       style={{ tableLayout: "fixed", width: "100%", minWidth: "1022px" }}
  //                     >
  //                       <colgroup>
  //                         <col style={{ width: "10%" }} />
  //                         <col style={{ width: "34%" }} />
  //                         <col style={{ width: "8%" }} />
  //                         <col style={{ width: "10%" }} />
  //                         <col style={{ width: "12%" }} />
  //                         <col style={{ width: "10%" }} />
  //                       </colgroup>

  //                       <thead className="table-light sticky-top" style={{ top: 0, zIndex: 2 }}>
  //                         {table.getHeaderGroups().map((hg) => (
  //                           <tr key={hg.id}>
  //                             {hg.headers.map((header) => (
  //                               <th key={header.id} className="table-headers">
  //                                 {header.isPlaceholder
  //                                   ? null
  //                                   : flexRender(
  //                                       header.column.columnDef.header,
  //                                       header.getContext()
  //                                     )}
  //                               </th>
  //                             ))}
  //                           </tr>
  //                         ))}
  //                       </thead>

  //                       <tbody>
  //                         {table.getRowModel().rows.map((row) => {
  //                           const productobj = row.original as ProductObj;
  //                           const p = productobj?.product;
  //                           const variants: Variant[] = p?.variants || [];
  //                           const rowId = getRowId(productobj, row.index);
  //                           const isExpanded = expandedRowId === rowId;

  //                           const hasAttributes = variants.some(
  //                             (v) => v?.attributes && Object.keys(v.attributes).length > 0
  //                           );
  //                           const hasConfig = hasAttributes;

  //                           const availableAttributes = availableAttributesByRow[rowId] || {};
  //                           const selectedAttributes = selectedAttributesByRow[rowId] || {};
  //                           const selectedVariant = selectedVariantByRow[rowId];

  //                           const customizationFields: CustomField[] = ((): CustomField[] => {
  //                             if (selectedVariant?.customizations?.fields?.length)
  //                               return selectedVariant.customizations.fields;
  //                             if (variants[0]?.customizations?.fields?.length)
  //                               return variants[0].customizations!.fields;
  //                             if (productobj?.customizations?.fields?.length)
  //                               return productobj.customizations!.fields!;
  //                             return [];
  //                           })();

  //                           const customValues = rowCustomValues[rowId] || {};
  //                           const filePreviews = rowFilePreviews[rowId] || {};

  //                           return (
  //                             <React.Fragment key={row.id}>
  //                               {/* main row */}
  //                               <tr>
  //                                 {row.getVisibleCells().map((cell) => (
  //                                   <td key={cell.id}>
  //                                     {flexRender(
  //                                       cell.column.columnDef.cell,
  //                                       cell.getContext()
  //                                     )}
  //                                   </td>
  //                                 ))}
  //                               </tr>

  //                               {/* expanded panel (customize) */}
  //                               {isExpanded && hasConfig && (
  //                                 <tr>
  //                                   <td colSpan={row.getVisibleCells().length}>
  //                                     <div className="p-3 border-top bg-light-subtle">
  //                                       {customizationFields.length > 0 && (
  //                                         <div className="mb-2">
  //                                           <div className="fw-semibold small mb-2">
  //                                             Product customization
  //                                           </div>
  //                                        <div className="row">
  //     {customizationFields.map((field) => {
  //       const key = field._id || field.field_name;

  //       // prefer the actual field value if present (often an id); else fallback to saved preview map
  //     const fileVal = customValues[field.field_name];
  //     const idOrUrl =
  //       filePreviews[field.field_name] || (typeof fileVal === "string" ? fileVal : "");
  //     const previewSrc = resolvePreviewSrc(idOrUrl);


  //       return (
  //         <div className="col-md-4 mb-3" key={key}>
  //           <label className="form-label">
  //             {field.field_name} {field.required && <span className="text-danger">*</span>}
  //           </label>

  //           {field.field_type === "text" ? (
  //             <input
  //               type="text"
  //               className="form-control"
  //               value={(customValues[field.field_name] as string) ?? ""}
  //               onChange={(e) =>
  //                 handleTextChange(productobj, rowId, field, e.target.value)
  //               }
  //             />
  //           ) : (
  //             <>
  //               <input
  //                 type="file"
  //                 accept="image/*"
  //                 className="form-control"
  //                 onChange={(e) => {
  //                   const file = e.currentTarget.files?.[0] || null;
  //                   handleFileChange(productobj, rowId, field, file);
  //                 }}
  //               />

  //               {previewSrc && (
  //                 <div
  //                   className="position-relative mt-2"
  //                   style={{ width: 120, height: "auto", display: "inline-block" }}
  //                 >
  //                   <img
  //                     src={previewSrc}
  //                     alt="Preview"
  //                     style={{ maxWidth: 120, borderRadius: 6, display: "block" }}
  //                   />
  //                   <button
  //                     type="button"
  //                     className="btn btn-sm btn-light rounded-circle position-absolute top-0 end-0 p-0"
  //                     style={{ width: 24, height: 24, lineHeight: 1 }}
  //                     title="Remove"
  //                     onClick={() => handleRemoveCustomFile(productobj, rowId, field)}
  //                   >
  //                     <i className="bi bi-x-lg" />
  //                   </button>
  //                 </div>
  //               )}
  //             </>
  //           )}
  //         </div>
  //       );
  //     })}
  //   </div>

  //                                         </div>
  //                                       )}

  //                                       {/* footer actions */}
  //                                       {(() => {
  //                                         const hasCustomizations =
  //                                           variants.some(
  //                                             (v) =>
  //                                               Array.isArray(v?.customizations?.fields) &&
  //                                               v.customizations.fields.length > 0
  //                                           ) ||
  //                                           (Array.isArray(productobj?.customizations?.fields) &&
  //                                             productobj.customizations.fields.length > 0);

  //                                         const needsCfg = hasAttributes || hasCustomizations;
  //                                         const canSubmitThisRow = needsCfg
  //                                           ? expandedRowId === rowId && !!rowValidity[rowId]
  //                                           : true;

  //                                         return (
  //                                          <div className="d-flex gap-4 mt-2">
  //                                         <button type="button" className="delete-btn br-8" onClick={() => setExpandedRowId(null)}>
  //                                           Cancel
  //                                         </button>
  //                                         <button
  //                                           type="button"
  //                                           className="btn btn-primary table-addbutton br-8"
  //                                           onClick={() => handleAddConfigured(productobj)}
  //                                         >
  //                                           Add
  //                                         </button>
  //                                       </div>
  //                                         );
  //                                       })()}
  //                                     </div>
  //                                   </td>
  //                                 </tr>
  //                               )}
  //                             </React.Fragment>
  //                           );
  //                         })}

  //                         {table.getRowModel().rows.length === 0 && (
  //                           <tr>
  //                             <td className="text-center py-4 text-muted" colSpan={table.getAllLeafColumns().length}>
  //                               No products found.
  //                             </td>
  //                           </tr>
  //                         )}
  //                       </tbody>
  //                     </table>
  //                   </div>
  //                 </div>

  //                 {/* Mobile View (≤1021px) */}
  //                 <div className="rdt-mobile">
  //                   <div
  //                     className="product-picker-wrap"
  //                     style={{ maxHeight: 480, overflowY: "auto" }}
  //                   >
  //                     {table.getRowModel().rows.length === 0 ? (
  //                       <div className="text-center py-4 text-muted">
  //                         No products found.
  //                       </div>
  //                     ) : (
  //                       <div className="d-flex flex-column gap-3 p-2">
  //                         {table.getRowModel().rows.map((row) => {
  //                           const productobj = row.original as ProductObj;
  //                           const p = productobj?.product;
  //                           const variants: Variant[] = p?.variants || [];
  //                           const rowId = getRowId(productobj, row.index);
  //                           const isExpanded = expandedRowId === rowId;

  //                           const hasAttributes = variants.some(
  //                             (v) => v?.attributes && Object.keys(v.attributes).length > 0
  //                           );
  //                           const hasConfig = hasAttributes;

  //                           const availableAttributes = availableAttributesByRow[rowId] || {};
  //                           const selectedAttributes = selectedAttributesByRow[rowId] || {};
  //                           const selectedVariant = selectedVariantByRow[rowId];

  //                           const customizationFields: CustomField[] = ((): CustomField[] => {
  //                             if (selectedVariant?.customizations?.fields?.length)
  //                               return selectedVariant.customizations.fields;
  //                             if (variants[0]?.customizations?.fields?.length)
  //                               return variants[0].customizations!.fields;
  //                             if (productobj?.customizations?.fields?.length)
  //                               return productobj.customizations!.fields!;
  //                             return [];
  //                           })();

  //                           const customValues = rowCustomValues[rowId] || {};
  //                           const filePreviews = rowFilePreviews[rowId] || {};

  //                           const cells = row.getVisibleCells();
  //                           const imageCell = cells.find((c) => c.column.id === "image");
  //                           const nameCell = cells.find((c) => c.column.id === "name");
  //                           const categoryCell = cells.find((c) => c.column.id === "category");
  //                           const attributesCell = cells.find((c) => c.column.id === "attributes");
  //                           const customCell = cells.find((c) => c.column.id === "custom");
  //                           const actionsCell = cells.find((c) => c.column.id === "actions");

  //                           return (
  //                             <div key={row.id} className="card border">
  //                               <div className="card-body">
  //                                 {/* Top section: Image + Name + Actions */}
  //                                 <div className="d-flex align-items-start gap-3 mb-3">
  //                                   {imageCell && (
  //                                     <div style={{ flexShrink: 0 }}>
  //                                       {flexRender(imageCell.column.columnDef.cell, imageCell.getContext())}
  //                                     </div>
  //                                   )}
  //                                   <div className="flex-grow-1">
  //                                     {nameCell && (
  //                                       <div className="mb-2">
  //                                         {flexRender(nameCell.column.columnDef.cell, nameCell.getContext())}
  //                                       </div>
  //                                     )}
  //                                   </div>
  //                                   {actionsCell && (
  //                                     <div style={{ flexShrink: 0 }}>
  //                                       {flexRender(actionsCell.column.columnDef.cell, actionsCell.getContext())}
  //                                     </div>
  //                                   )}
  //                                 </div>

  //                                 {/* Other fields */}
  //                                 {categoryCell && (
  //                                   <div className="mb-2">
  //                                     <div className="text-muted small mb-1">Category</div>
  //                                     <div>{flexRender(categoryCell.column.columnDef.cell, categoryCell.getContext())}</div>
  //                                   </div>
  //                                 )}

  //                                 {attributesCell && (
  //                                   <div className="mb-2">
  //                                     <div className="text-muted small mb-1">Attributes</div>
  //                                     <div>{flexRender(attributesCell.column.columnDef.cell, attributesCell.getContext())}</div>
  //                                   </div>
  //                                 )}

  //                                 {customCell && (
  //                                   <div className="mb-2">
  //                                     {flexRender(customCell.column.columnDef.cell, customCell.getContext())}
  //                                   </div>
  //                                 )}

  //                                 {/* Expanded panel (customize) */}
  //                                 {isExpanded && hasConfig && (
  //                                   <div className="mt-3 pt-3 border-top">
  //                                     {customizationFields.length > 0 && (
  //                                       <div className="mb-2">
  //                                         <div className="fw-semibold small mb-2">
  //                                           Product customization
  //                                         </div>
  //                                         <div className="row">
  //                                           {customizationFields.map((field) => {
  //                                             const key = field._id || field.field_name;
  //                                             const fileVal = customValues[field.field_name];
  //                                             const idOrUrl =
  //                                               filePreviews[field.field_name] || (typeof fileVal === "string" ? fileVal : "");
  //                                             const previewSrc = resolvePreviewSrc(idOrUrl);

  //                                             return (
  //                                               <div className="col-12 col-md-6 mb-3" key={key}>
  //                                                 <label className="form-label">
  //                                                   {field.field_name} {field.required && <span className="text-danger">*</span>}
  //                                                 </label>

  //                                                 {field.field_type === "text" ? (
  //                                                   <input
  //                                                     type="text"
  //                                                     className="form-control"
  //                                                     value={(customValues[field.field_name] as string) ?? ""}
  //                                                     onChange={(e) =>
  //                                                       handleTextChange(productobj, rowId, field, e.target.value)
  //                                                     }
  //                                                   />
  //                                                 ) : (
  //                                                   <>
  //                                                     <input
  //                                                       type="file"
  //                                                       accept="image/*"
  //                                                       className="form-control"
  //                                                       onChange={(e) => {
  //                                                         const file = e.currentTarget.files?.[0] || null;
  //                                                         handleFileChange(productobj, rowId, field, file);
  //                                                       }}
  //                                                     />

  //                                                     {previewSrc && (
  //                                                       <div
  //                                                         className="position-relative mt-2"
  //                                                         style={{ width: 120, height: "auto", display: "inline-block" }}
  //                                                       >
  //                                                         <img
  //                                                           src={previewSrc}
  //                                                           alt="Preview"
  //                                                           style={{ maxWidth: 120, borderRadius: 6, display: "block" }}
  //                                                         />
  //                                                         <button
  //                                                           type="button"
  //                                                           className="btn btn-sm btn-light rounded-circle position-absolute top-0 end-0 p-0"
  //                                                           style={{ width: 24, height: 24, lineHeight: 1 }}
  //                                                           title="Remove"
  //                                                           onClick={() => handleRemoveCustomFile(productobj, rowId, field)}
  //                                                         >
  //                                                           <i className="bi bi-x-lg" />
  //                                                         </button>
  //                                                       </div>
  //                                                     )}
  //                                                   </>
  //                                                 )}
  //                                               </div>
  //                                             );
  //                                           })}
  //                                         </div>
  //                                       </div>
  //                                     )}

  //                                     {/* footer actions */}
  //                                     {(() => {
  //                                       const hasCustomizations =
  //                                         variants.some(
  //                                           (v) =>
  //                                             Array.isArray(v?.customizations?.fields) &&
  //                                             v.customizations.fields.length > 0
  //                                         ) ||
  //                                         (Array.isArray(productobj?.customizations?.fields) &&
  //                                           productobj.customizations.fields.length > 0);

  //                                       const needsCfg = hasAttributes || hasCustomizations;
  //                                       const canSubmitThisRow = needsCfg
  //                                         ? expandedRowId === rowId && !!rowValidity[rowId]
  //                                         : true;

  //                                       return (
  //                                         <div className="d-flex gap-4 mt-2">
  //                                           <button type="button" className="delete-btn br-8" onClick={() => setExpandedRowId(null)}>
  //                                             Cancel
  //                                           </button>
  //                                           <button
  //                                             type="button"
  //                                             className="btn btn-primary table-addbutton br-8"
  //                                             onClick={() => handleAddConfigured(productobj)}
  //                                           >
  //                                             Add
  //                                           </button>
  //                                         </div>
  //                                       );
  //                                     })()}
  //                                   </div>
  //                                 )}
  //                               </div>
  //                             </div>
  //                           );
  //                         })}
  //                       </div>
  //                     )}
  //                   </div>
  //                 </div>
  //               </>
  //             )}
  //           </div>

  //           {/* footer pagination */}
  //           {table.getRowModel().rows.length === 0 ? (
  //             <div className="text-center my-4 text-muted" style={{ fontSize: "1.05rem" }}>
  //               No records found
  //             </div>
  //           ) : (
  //             <div className="px-4 pb-4 pt-2 border-top bg-white">
  //               <PaginationControls table={table} />
  //             </div>
  //           )}
  //         </div>
  //       </div>

  //       {/* quick styles for the refined look */}
  //       <style>{`
  //         .product-picker-table thead th { font-weight: 600; color:#111827; }
  //         .product-picker-table tbody td { vertical-align: middle; }
  //         .table-headers { white-space: nowrap; }
  //         .table-cells { font-size: 0.92rem; }
  //         .xsmall { font-size: .75rem; }
  //         .br-8 { border-radius: 8px; }
  //         .search-wrap input { min-width: 260px; }
  //         @media (max-width: 576px) {
  //           .search-wrap input { min-width: 180px; }
  //         }
  //         /* Responsive table/card visibility */
  //         .rdt-desktop { display: block; }
  //         .rdt-mobile { display: none; }
  //         @media (max-width: 1021px) {
  //           .rdt-desktop { display: none; }
  //           .rdt-mobile { display: block; }
  //         }
  //         /* Delete Button */
  //         .delete-btn {
  //           padding: 8px 22px;
  //           background: #e7000b;
  //           border: none;
  //           border-radius: 8px;
  //           font-size: 15px;
  //           cursor: pointer;
  //           color: white;
  //           font-weight: 500;
  //           transition: background 0.2s ease;
  //         }
  //         .delete-btn:hover {
  //           background: #c10007;
  //         }

  //         @keyframes fadeIn {
  //           from { opacity: 0; transform: translateY(-4px); }
  //           to { opacity: 1; transform: translateY(0); }
  //         }
  //           /* Close button UI (like your 2nd image hover) */
  // .modal-close-btn{
  //   position: absolute;
  //   top: 18px;
  //   right: 18px;
  //   width: 44px;
  //   height: 44px;
  //   border-radius: 12px;
  //   border: 1px solid #e5e7eb;
  //   background: #f9fafb;
  //   color: #111827;
  //   display: inline-flex;
  //   align-items: center;
  //   justify-content: center;
  //   transition: all .18s ease;
  // }

  // .modal-close-btn i{
  //   font-size: 20px;
  //   line-height: 1;
  // }

  // /* hover look (red border + light red background) */
  // .modal-close-btn:hover{
  //   background: #fff1f2;
  //   border-color: #fda4af;
  //   color: #e11d48;
  // }
  // /* Search bar like screenshot */
  // .po-search-pill{
  //   position: relative;
  //   height: 44px;
  //   border: 1px solid #d1d5db;
  //   border-radius: 12px;
  //   background: #fff;
  //   box-shadow: 0 1px 2px rgba(0,0,0,.06);
  //   display: flex;
  //   align-items: center;
  //   overflow: hidden;
  // }

  // .po-search-icon{
  //   position: absolute;
  //   left: 14px;
  //   top: 50%;
  //   transform: translateY(-50%);
  //   color: #9ca3af;
  //   font-size: 16px;
  //   pointer-events: none;
  // }

  // .po-search-input{
  //   width: 100%;
  //   height: 100%;
  //   border: 0 !important;
  //   outline: none !important;
  //   background: transparent;
  //   padding: 0 14px 0 42px;   /* left space for icon */
  //   font-size: 13px;
  //   color: #111827;
  // }

  // .po-search-input::placeholder{
  //   color: #9ca3af;
  // }

  // /* focus like modern input (optional but nice) */
  // .po-search-pill:focus-within{
  //   border-color: #cbd5e1;
  //   box-shadow: 0 0 0 4px rgba(59,130,246,.10);
  // }
  // /* =======================
  //    MODAL LOOK (HTML STYLE)
  // ======================= */
  // #checkoutpopup {
  //   border-radius: 12px !important;
  //   box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
  // }

  // /* Header */
  // .pp-modal-header {
  //   padding: 28px 32px;
  //   border-bottom: 1px solid #e5e7eb;
  //   background: linear-gradient(to bottom, #ffffff, #fafbfc);
  //   display: flex;
  //   flex-direction: column;
  //   gap: 20px;
  //   position: relative;
  // }

  // .pp-header-top {
  //   display: flex;
  //   align-items: center;
  //   justify-content: center;
  //   position: relative;
  // }

  // .pp-modal-title {
  //   font-size: 22px;
  //   font-weight: 600;
  //   color: #111827;
  //   letter-spacing: -0.02em;
  //   margin: 0;
  // }

  // .pp-close-btn {
  //   position: absolute;
  //   right: 0;
  //   width: 36px;
  //   height: 36px;
  //   border-radius: 8px;
  //   border: none;
  //   background: #f3f4f6;
  //   color: #6b7280;
  //   cursor: pointer;
  //   display: flex;
  //   align-items: center;
  //   justify-content: center;
  //   transition: all 0.2s;
  // }

  // .pp-close-btn:hover {
  //   background: #fee2e2;
  //   color: #dc2626;
  //   transform: scale(1.05);
  // }

  // .pp-header-search {
  //   display: flex;
  //   justify-content: center;
  // }

  // .pp-search-box {
  //   position: relative;
  //   width: 100%;
  //   max-width: 560px;
  // }

  // .pp-search-icon {
  //   position: absolute;
  //   left: 16px;
  //   top: 50%;
  //   transform: translateY(-50%);
  //   color: #9ca3af;
  //   pointer-events: none;
  //   font-size: 18px;
  // }

  // .pp-search-input {
  //   width: 100%;
  //   padding: 12px 20px 12px 46px;
  //   border: 1.5px solid #d1d5db;
  //   border-radius: 10px;
  //   font-size: 15px;
  //   transition: all 0.2s;
  //   background: #fff;
  // }

  // .pp-search-input:focus {
  //   outline: none;
  //   border-color: #3b82f6;
  //   box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  // }

  // .pp-search-input::placeholder {
  //   color: #9ca3af;
  // }

  // /* =======================
  //    TABLE STYLE (HTML STYLE)
  // ======================= */
  // .pp-table-container {
  //   flex: 1;
  //   max-height: 520px;           /* you can adjust */
  //   overflow-y: auto;
  //   overflow-x: auto;
  // }

  // /* scrollbar */
  // .pp-table-container::-webkit-scrollbar {
  //   width: 10px;
  //   height: 10px;
  // }
  // .pp-table-container::-webkit-scrollbar-track {
  //   background: #f3f4f6;
  // }
  // .pp-table-container::-webkit-scrollbar-thumb {
  //   background: #d1d5db;
  //   border-radius: 5px;
  // }
  // .pp-table-container::-webkit-scrollbar-thumb:hover {
  //   background: #9ca3af;
  // }

  // .pp-table {
  //   width: 100%;
  //   border-collapse: collapse;
  // }

  // /* sticky head */
  // .pp-table thead {
  //   position: sticky;
  //   top: 0;
  //   z-index: 10;
  //   background: white;
  // }
  // .pp-table thead tr {
  //   border-bottom: 2px solid #e5e7eb;
  // }
  // .pp-table th {
  //   padding: 16px 20px;
  //   text-align: left;
  //   font-size: 13px;
  //   font-weight: 600;
  //   color: #6b7280;
  //   text-transform: uppercase;
  //   letter-spacing: 0.05em;
  //   background: #f9fafb;
  //   white-space: nowrap;
  // }

  // .pp-table td {
  //   padding: 24px 20px;
  //   font-size: 14px;
  //   color: #374151;
  //   vertical-align: middle;
  // }

  // /* row hover */
  // .pp-table tbody tr {
  //   border-bottom: 1px solid #f3f4f6;
  //   transition: all 0.2s;
  // }
  // .pp-table tbody tr:hover {
  //   background: #fafbfc;
  //   box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  // }

  // /* Actions column align right */
  // .pp-table th:last-child,
  // .pp-table td:last-child {
  //   text-align: right;
  // }

  // /* Add button look */
  // .pp-add-btn {
  //   padding: 11px 28px;
  //   background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  //   color: white;
  //   border: none;
  //   border-radius: 8px;
  //   font-size: 14px;
  //   font-weight: 600;
  //   cursor: pointer;
  //   transition: all 0.2s;
  //   box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  // }

  // .pp-add-btn:hover {
  //   background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  //   box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
  //   transform: translateY(-2px);
  // }

  // /* Category badge */
  // .pp-category-badge {
  //   display: inline-flex;
  //   align-items: center;
  //   padding: 7px 14px;
  //   background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  //   color: #1e40af;
  //   border-radius: 7px;
  //   font-size: 13px;
  //   font-weight: 600;
  //   border: 1px solid #bfdbfe;
  // }

  // /* Attribute select look (if you use native select) */
  // .pp-attribute-select {
  //   width: 100%;
  //   padding: 8px 32px 8px 12px;
  //   border: 1.5px solid #e5e7eb;
  //   border-radius: 6px;
  //   font-size: 13px;
  //   color: #374151;
  //   background: #fff;
  //   cursor: pointer;
  // }
  // .pp-attribute-select:focus {
  //   outline: none;
  //   border-color: #3b82f6;
  //   box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
  // }


  //       `}</style>
  //     </div>
  //   );
  // };
}
export default OrderCheckoutPop;

